/**
 * Actions for the lexicons module (VE-24948 / U5).
 *
 * Plain `createAction` rather than the `createGraphQLSuccessAction` helpers used
 * by `redactionCodes`: lexicon transport goes through an injected
 * `GraphQLExecutor` (see `services/executor.ts`) so the saga owns its own
 * loading and error state. Wrapping the transport in a second layer of
 * request/success/failure actions would duplicate that, and it is what makes
 * the fail-closed mutation sequence (ADR-003) testable as one unit rather than
 * as a chain of dispatches.
 */

import { createAction } from '@reduxjs/toolkit';

import {
  Lexicon,
  LexiconCsvValidationResult,
  LexiconEntryInput,
  LexiconSummary,
} from '../models';

const type = (name: string) => `LEXICONS/${name}`;

// --- Listing -----------------------------------------------------------------

/**
 * Every LIVE lexicon in the org. No payload, and that is the point.
 *
 * It used to take none either, and instead read `showDeleted`, `offset` and
 * `limit` out of the store — an argument the call site never passed and could
 * not see. The settings page owns that state, it outlives the page, and so the
 * editor's fetch inherited the trash after a visit to Deleted Lexicons:
 * `selectApplicableLexicons` dropped every row and the Lexicon button vanished
 * (VE-30783). WHICH LIST IS THE ACTION'S NAME NOW — this one or
 * `FETCH_DELETED_LEXICONS`, never a flag and never a value read from the store.
 *
 * The whole library rather than a page, so paging and sorting belong to the
 * table, over rows it already holds. `fetchAllLiveLexicons` reads it in pages
 * of `LEXICON_SCAN_PAGE_SIZE` and stops when the server reports nothing beyond
 * the page it served, so an org inside one page costs exactly one request.
 */
export const FETCH_LEXICONS = createAction(type('FETCH_LEXICONS'));

/** Every live lexicon in the org, replacing whatever the store held. */
export const FETCH_LEXICONS_SUCCESS = createAction<{
  lexicons: readonly LexiconSummary[];
}>(type('FETCH_LEXICONS_SUCCESS'));

/**
 * Every SOFT-DELETED lexicon in the org — the trash view, and the "(N)" on the
 * link that leads to it.
 *
 * Fetched beside the library on the settings page and nowhere else, which is
 * the point of it being its own action: the editor never asks, so the trash
 * never crosses the wire for a picker. The count is this list's length; it used
 * to be `countDeletedLexicons`, a bounded query that fetched 101 rows to report
 * a number and threw the rows away, capped at "100+" (VE-29495).
 */
export const FETCH_DELETED_LEXICONS = createAction(
  type('FETCH_DELETED_LEXICONS')
);

/** Every soft-deleted lexicon in the org, replacing whatever the store held. */
export const FETCH_DELETED_LEXICONS_SUCCESS = createAction<{
  lexicons: readonly LexiconSummary[];
}>(type('FETCH_DELETED_LEXICONS_SUCCESS'));

export const FETCH_DELETED_LEXICONS_FAILURE = createAction<{ message: string }>(
  type('FETCH_DELETED_LEXICONS_FAILURE')
);

/**
 * Drops the trash from the store. Dispatched when the Lexicons page unmounts.
 *
 * The deleted list is the settings page's alone — one consumer, `LexiconTable`
 * — and the page refetches it on mount, so keeping it after the user leaves
 * serves nothing. What it costs is that every deleted lexicon's name and its
 * `createdBy` / `modifiedBy` EMAIL ADDRESSES stay resident for the rest of the
 * session, including in the document editor and in anything that serialises
 * the store. Data minimisation, not a vulnerability: the rows are same-org and
 * the server already authorised them.
 *
 * The LIVE list is deliberately NOT cleared. The editor's pickers read it, and
 * the editor route fetches it for exactly that reason.
 *
 * IT ALSO LOWERS `isFetchingDeleted`, and that is the load-bearing half. This
 * does not cancel the fetch — the watcher is global and a mutation's trailing
 * refetch is dispatched AFTER the unmount cleanup rather than racing it — so a
 * response landing later would otherwise seat the whole trash back in the
 * store, which is the exact state this action exists to prevent. The lowered
 * flag is what `FETCH_DELETED_LEXICONS_SUCCESS` checks before writing.
 *
 * What it cannot do is break anything by running late, unlike the view reset
 * this page tried in VE-30783's first attempt: it clears rows a table renders,
 * not a value a saga reads as a filter.
 */
export const CLEAR_DELETED_LEXICONS = createAction(
  type('CLEAR_DELETED_LEXICONS')
);

/**
 * Re-reads the lexicon the drawer is editing, WITH its entries.
 *
 * The BR-6 conflict banner's Reload. It used to be `FETCH_LEXICONS`, because
 * the list carried entries and the reducer could rebuild the draft from it —
 * the list carries summaries now, so taking the other actor's version needs a
 * read of its own.
 */
/**
 * Downloads one lexicon as CSV.
 *
 * An action rather than a click handler because the row only has a SUMMARY —
 * the entries the file is made of have to be read first, and reading is a
 * saga's job.
 */
export const EXPORT_LEXICON = createAction<{ lexiconId: string }>(
  type('EXPORT_LEXICON')
);

export const RELOAD_EDITING_LEXICON = createAction(
  type('RELOAD_EDITING_LEXICON')
);

export const RELOAD_EDITING_LEXICON_SUCCESS = createAction<{
  lexicon: Lexicon;
}>(type('RELOAD_EDITING_LEXICON_SUCCESS'));

export const FETCH_LEXICONS_FAILURE = createAction<{ message: string }>(
  type('FETCH_LEXICONS_FAILURE')
);

/**
 * Lexicons are not provisioned in this environment (NFR-U5-12).
 *
 * A separate action from FAILURE on purpose: this is a legitimate state that
 * hides the feature, not an error to retry. GOV before its bootstrap runs is
 * exactly this.
 */
export const LEXICONS_NOT_CONFIGURED = createAction(
  type('LEXICONS_NOT_CONFIGURED')
);

// --- Drawer / draft ----------------------------------------------------------

/** Opens the drawer. Omit `lexiconId` to create. */
export const OPEN_LEXICON_DRAWER = createAction<{ lexiconId?: string }>(
  type('OPEN_LEXICON_DRAWER')
);

export const CLOSE_LEXICON_DRAWER = createAction(type('CLOSE_LEXICON_DRAWER'));

export const SET_DRAFT_NAME = createAction<{ name: string }>(
  type('SET_DRAFT_NAME')
);

export const SET_DRAFT_NAME_ERROR = createAction<{ message?: string }>(
  type('SET_DRAFT_NAME_ERROR')
);

/**
 * Adds entries to the OPEN draft (not to the lexicon). Duplicates of entries
 * already in the draft are skipped, and the draft is capped at BR-16.
 */
export const ADD_DRAFT_ENTRIES = createAction<{
  entries: readonly LexiconEntryInput[];
}>(type('ADD_DRAFT_ENTRIES'));

/**
 * Replaces one entry in the OPEN draft, IN PLACE.
 *
 * In place is the point: the entry keeps its id and its position, so an edit
 * reads as an edit rather than as a delete followed by an append that jumps the
 * row to the bottom of the list. `selectDraftDiff` compares the result against
 * the open-time baseline and reports it as `updated`.
 */
export const UPDATE_DRAFT_ENTRY = createAction<{
  entryId: string;
  entry: LexiconEntryInput;
}>(type('UPDATE_DRAFT_ENTRY'));

/**
 * Deletes one entry from the OPEN draft.
 *
 * SOFT for an entry that has been saved before — it is tombstoned with
 * `isDeleted` and stays in the draft, so the drawer can offer it back. The
 * delete control is an unguarded icon on every row and losing a term to one
 * stray click is otherwise silent.
 *
 * HARD for an entry that only exists in this draft (a `draft:` id). There is
 * nothing to recover — it was never saved — and a tombstone for it would be
 * noise in the deleted view and an item against the schema's `maxItems`.
 */
export const REMOVE_DRAFT_ENTRY = createAction<{ entryId: string }>(
  type('REMOVE_DRAFT_ENTRY')
);

/** Clears the tombstone on one entry, putting it back in the live list. */
export const RESTORE_DRAFT_ENTRY = createAction<{ entryId: string }>(
  type('RESTORE_DRAFT_ENTRY')
);

/** Drops ONE tombstone for good, from the deleted view's own trash control. */
export const PURGE_DRAFT_ENTRY = createAction<{ entryId: string }>(
  type('PURGE_DRAFT_ENTRY')
);

/**
 * Drops every tombstone in the draft for good.
 *
 * The only thing that reclaims room under the schema's `maxItems: 5000`, which
 * counts tombstones like any other item. Irreversible, so the drawer puts a
 * confirmation in front of it — unlike the per-row delete, which is exactly the
 * unguarded control this whole mechanism exists to make survivable.
 */
export const PURGE_DELETED_DRAFT_ENTRIES = createAction(
  type('PURGE_DELETED_DRAFT_ENTRIES')
);

/**
 * Commits the open draft in one Save: create (name + entries) when the draft
 * has no lexicon yet, otherwise rename / append / remove as needed — each
 * step its own audited mutation (ADR-003). The saga reads the draft from the
 * store, so the payload is empty.
 */
export const SAVE_LEXICON_DRAFT = createAction(type('SAVE_LEXICON_DRAFT'));

/** Reported by the service when a write lost a revision race (BR-6). */
export const SET_LEXICON_CONFLICT = createAction<{
  modifiedBy: string;
  modifiedDateTime: string;
}>(type('SET_LEXICON_CONFLICT'));

export const CLEAR_LEXICON_CONFLICT = createAction(
  type('CLEAR_LEXICON_CONFLICT')
);

// --- Mutations ---------------------------------------------------------------

export const CREATE_LEXICON = createAction<{ name: string }>(
  type('CREATE_LEXICON')
);

export const RENAME_LEXICON = createAction<{
  lexiconId: string;
  name: string;
}>(type('RENAME_LEXICON'));

export const ADD_LEXICON_ENTRIES = createAction<{
  lexiconId: string;
  entries: readonly LexiconEntryInput[];
}>(type('ADD_LEXICON_ENTRIES'));

export const REMOVE_LEXICON_ENTRIES = createAction<{
  lexiconId: string;
  entryIds: readonly string[];
}>(type('REMOVE_LEXICON_ENTRIES'));

/**
 * Flips a library flag from the settings table.
 *
 * ONE action for both checkboxes rather than one each, because they are one
 * write: a partial payload says which flags this change touches, and the
 * service leaves the others exactly as it found them. The alternative — two
 * actions, two sagas, two near-identical writes — is how the watermark module
 * ended up with `SET_WATERMARK_ENABLED` and `SET_WATERMARK_AUTO_APPLY` doing
 * the same thing twice.
 *
 * The reducer applies it OPTIMISTICALLY. A checkbox that waits for a round trip
 * before moving reads as broken, and a failure re-fetches the page, so the
 * worst case is a tick that flicks back with a notice beside it.
 */
export const SET_LEXICON_FLAGS = createAction<{
  lexiconId: string;
  flags: { enabled?: boolean; autoApply?: boolean };
}>(type('SET_LEXICON_FLAGS'));

/**
 * The table's Delete — a SOFT delete since BR-5 was amended.
 *
 * The name is unchanged on purpose: from the user's side this is still "delete
 * the lexicon", and every call site, the confirm dialog and the audit kind
 * (`lexicon.delete`) mean the same thing they always did. What changed is what
 * the registry is asked to do — raise `isDeleted` rather than remove records.
 */
export const DELETE_LEXICON = createAction<{ lexiconId: string }>(
  type('DELETE_LEXICON')
);

/** Undoes a soft delete. Refused when a live lexicon has taken the name. */
export const RESTORE_LEXICON = createAction<{ lexiconId: string }>(
  type('RESTORE_LEXICON')
);

/**
 * PERMANENT delete, from the deleted view only.
 *
 * The one irreversible operation in the feature, and the only action whose name
 * says so. Both records go — the summary and its entries.
 */
export const PURGE_LEXICON = createAction<{ lexiconId: string }>(
  type('PURGE_LEXICON')
);

/**
 * A lexicon crossed the soft-delete line: the row changes lists.
 *
 * Dispatched by the soft delete and the restore, which are the only writes that
 * flip `isDeleted`, and dispatched BEFORE their refetches. That ordering is the
 * point. The two lists live in two store slots, so a row changing sides is a
 * removal from one and an insertion into the other — and leaving that to the
 * refetches made it depend on two independent requests both landing. If the
 * deleted one failed after a restore, the row showed in BOTH tables, with
 * Permanently delete live on a lexicon that was no longer deleted.
 *
 * It carries a `LexiconSummary` rather than riding on `LEXICON_MUTATION_SUCCESS`
 * because `setLexiconFlags` returns a summary: no entries, so the entry counts
 * that action derives from `lexicon.entries` would come back zero.
 */
export const LEXICON_SOFT_DELETE_CHANGED = createAction<{
  lexicon: LexiconSummary;
}>(type('LEXICON_SOFT_DELETE_CHANGED'));

export const LEXICON_MUTATION_SUCCESS = createAction<{ lexicon?: Lexicon }>(
  type('LEXICON_MUTATION_SUCCESS')
);

export const LEXICON_DELETED = createAction<{ lexiconId: string }>(
  type('LEXICON_DELETED')
);

/**
 * A mutation was refused because it could not be audited (NFR-S4).
 *
 * Distinct from a generic failure so the UI can say "not applied" rather than
 * leaving the user guessing whether it partly happened. Under SEC-R4 that
 * distinction is the whole point of failing closed.
 */
export const LEXICON_MUTATION_BLOCKED_BY_AUDIT = createAction<{
  message: string;
}>(type('LEXICON_MUTATION_BLOCKED_BY_AUDIT'));

/**
 * The write succeeded but its audit `outcome` event did not (ADR-003).
 *
 * The change DID happen, so this is not a failure — but the trail shows an
 * intent with no outcome and a reviewer may need to resolve it.
 */
export const LEXICON_MUTATION_AUDIT_INCOMPLETE = createAction<{
  operationId: string;
  message: string;
}>(type('LEXICON_MUTATION_AUDIT_INCOMPLETE'));

export const LEXICON_MUTATION_FAILURE = createAction<{ message: string }>(
  type('LEXICON_MUTATION_FAILURE')
);

/**
 * A mutation has finished, whatever the outcome.
 *
 * Clears `isMutating` and NOTHING else — a notice already set by
 * `LEXICON_MUTATION_FAILURE` / `_BLOCKED_BY_AUDIT` / `_AUDIT_INCOMPLETE` must
 * survive it, so this is dispatched last, from a `finally`.
 *
 * WHY IT EXISTS. Six mutation actions set `isMutating: true` in the reducer and
 * every one of them relied on a saga reaching one of the outcome actions to
 * clear it. Two shapes broke that: `getSagaContext` was called OUTSIDE each
 * saga's `try`, so a failure to resolve the lexicon schema or the auth context
 * cleared nothing, and `onSaveLexiconDraft` had an early `return` that
 * dispatched nothing at all. The result was a page where every mutation
 * control stayed disabled until reload — including the CSV dialog's Import
 * button, which is gated on `isMutating` and has nothing to do with the
 * mutation that got stuck.
 */
export const LEXICON_MUTATION_SETTLED = createAction(
  type('LEXICON_MUTATION_SETTLED')
);

/** Dismisses the mutation outcome notice the page is showing. */
export const CLEAR_MUTATION_NOTICE = createAction(
  type('CLEAR_MUTATION_NOTICE')
);

// --- CSV import --------------------------------------------------------------

export const START_CSV_IMPORT = createAction<{
  /** Absent while the lexicon is still being created (draft-only import). */
  lexiconId?: string;
  fileName: string;
  rowCount: number;
}>(type('START_CSV_IMPORT'));

export const SET_CSV_IMPORT_RESULT = createAction<{
  result: LexiconCsvValidationResult;
}>(type('SET_CSV_IMPORT_RESULT'));

export const CANCEL_CSV_IMPORT = createAction(type('CANCEL_CSV_IMPORT'));
